import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import { Locale } from "date-fns";
import { enUS } from "date-fns/locale";
import DatePicker from "react-datepicker";

import axios from "axios";
import { isValidPhoneNumber } from "react-phone-number-input";
import PhoneInput from "react-phone-number-input";

import { checkAvailability } from "./bookingUtils";
import {
  fetchCategories,
  PackageDTO,
} from "../../Booking/TourPackages/CategoryService";
import { BusinessUser, PrivateUser } from "../../CRUD/CampSites/types"; // Import interfaces
import { ManagerBookingRequest } from "./NewBookingManager";
import { toDateOnlyString, toDisplayDate } from "../../Utilities/date";

interface SimpleBookingFormProps {
  apiUrl: string;
}

const SimpleBookingForm: React.FC<SimpleBookingFormProps> = ({ apiUrl }) => {
  const navigate = useNavigate(); // Initialize useNavigate
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [categories, setCategories] = useState<any[]>([]);
  const [packages, setPackages] = useState<any[]>([]);
  const [packageId, setPackageId] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({ startDate: null, endDate: null });
  const [availability, setAvailability] = useState<any[]>([]);
  const [availabilityError, setAvailabilityError] = useState<string>("");
  const [submitError, setSubmitError] = useState<string>("");
  const [hasCheckedAvailability, setHasCheckedAvailability] = useState<boolean>(false);
  // Grouping UI state for availability (expand/collapse per group)
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [locale, setLocale] = useState<Locale>(enUS);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pricingModels, setPricingModels] = useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedPricingModel, setSelectedPricingModel] = useState<
    number | null
  >(null);
  const [localEmail, setLocalEmail] = useState<string>("");
  const [userType, setUserType] = useState<"private" | "business">("private");
  // Business bookings are saved by the backend at 20% off the gross package
  // price (BookingService.cs: `totalPrice *= 0.8m` for IsBusinessUser). The
  // staff-facing availability list and cart MUST show this same net price so
  // what the manager sees always matches what actually gets saved/invoiced.
  const BUSINESS_DISCOUNT_MULTIPLIER = 0.8;
  const getDisplayPrice = (price: number) =>
    userType === "business" ? price * BUSINESS_DISCOUNT_MULTIPLIER : price;
  // Prevents duplicate bookings from double-clicking "Confirm Bookings" while a slow request is in flight.
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [phoneNumberError, setPhoneNumberError] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [defaultCountry, setDefaultCountry] = useState<string>("US");
  const [userFound, setUserFound] = useState<boolean>(false);
  const [businessUser, setBusinessUser] = useState<BusinessUser>(
    {} as BusinessUser
  );
  const [privateUser, setPrivateUser] = useState<PrivateUser>(
    {} as PrivateUser
  );

  // Guest names state
  const [guestNamesInput, setGuestNamesInput] = useState<string>("");
  const [guestNames, setGuestNames] = useState<string[]>([""]); // Initialize with one empty string
  const [formVisible, setFormVisible] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedAvailability, setSelectedAvailability] = useState<
    number | null
  >(null);
  const [bookOutsidePackage, setBookOutsidePackage] = useState<boolean>(false);
  const [description, setDescription] = useState<string>("");

  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [availablePackages, setAvailablePackages] = useState<any[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<PackageDTO | null>(
    null
  );

  const [shoppingCart, setShoppingCart] = useState<ManagerBookingRequest[]>([]);
  const [selectedAvailabilities, setSelectedAvailabilities] = useState<
    number[]
  >([]);
  const [numberOfPersonsToAdd, setNumberOfPersonsToAdd] = useState<number>(1); // New state for number of persons when booking outside of a package
  const [numberOfNights, setNumberOfNights] = useState<number>(1); // Number of nights selector
  const [responseEmail, setResponseEmail] = useState<string>(""); // New state for email from the response
  const [chaletType, setChaletType] = useState<string>(""); // New state for chalet type
  const chaletTypes = [
    { label: "Big", value: 0 },
    { label: "Small", value: 1 },
  ]; // Define chalet types with enum values

  useEffect(() => {
    const loadCategories = async () => {
      const categories = await fetchCategories(apiUrl);
      setCategories(categories);
      const allPackages = categories.flatMap((category) => category.packages);
      setPackages(allPackages);
    };

    loadCategories();
  }, [apiUrl]);

  useEffect(() => {
    // Fetch available packages when the component mounts
    const fetchPackages = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/Package/getAllPackages`
        );
        const packages = (response.data as any[]).map((pkg: any) => pkg.data); // Extract the data property from each object
        setAvailablePackages(packages);
        console.log("Available packages:", packages);
      } catch (error) {
        console.error("Error fetching packages", error);
      }
    };

    fetchPackages();
  }, []);
  const handleCheckAvailability = async () => {
    if (dateRange.startDate && dateRange.endDate) {
      try {
        const adjustedStartDate = new Date(
          Date.UTC(
            dateRange.startDate.getFullYear(),
            dateRange.startDate.getMonth(),
            dateRange.startDate.getDate()
          )
        );
        const adjustedEndDate = new Date(
          Date.UTC(
            dateRange.endDate.getFullYear(),
            dateRange.endDate.getMonth(),
            dateRange.endDate.getDate()
          )
        );
        if (packageId !== null) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const token = localStorage.getItem("accessToken");
          const availabilityData = await checkAvailability(packageId, {
            startDate: adjustedStartDate,
            endDate: adjustedEndDate,
          });
          if (availabilityData === null) {
            setAvailabilityError(
              "No available pricing models found for the specified date range."
            );
            setAvailability([]);
          } else {
            // Expand rows: each available PM row becomes N selectable slots (one per available chalet)
            const expandedData = availabilityData.flatMap((item: any) => {
              const count = typeof item.availableCount === 'number' ? item.availableCount : 0;
              if (item.availabilityStatus === 'Available' && count > 0) {
                return Array.from({ length: count }, (_, i) => ({
                  ...item,
                  __slotNumber: i + 1,
                }));
              }
              // Fully booked or unavailable: keep as single row
              return [{ ...item, __slotNumber: 0 }];
            });
            setAvailability(expandedData);
            setAvailabilityError("");
          }
          setHasCheckedAvailability(true);
        } else {
          setAvailabilityError("Please select a package.");
          return;
        }
      } catch (error: any) {
        if (error.response && error.response.status === 401) {
          setAvailabilityError("Unauthorized. Please log in again.");
        } else if (
          error.response &&
          error.response.status === 400 &&
          error.response.data.message
        ) {
          setAvailabilityError(error.response.data.message);
        } else {
          setAvailabilityError(error.message);
        }
      }
    } else {
      setAvailabilityError("Please select a date range.");
    }
  };

  // Normalize chalet type: treat null/undefined as Big. Some APIs might return strings or numbers.
  const normalizeChaletType = (raw: any): "Big" | "Small" => {
    if (raw === null || raw === undefined) return "Big";
    // Accept 0/"0"/"Big"/0.0 as Big; 1/"1"/"Small" as Small
    const val = typeof raw === "string" ? raw.trim().toLowerCase() : raw;
    if (val === 0 || val === "0" || val === "big") return "Big";
    if (val === 1 || val === "1" || val === "small") return "Small";
    // Fallback
    return "Big";
  };

  // Build grouped availability by chalet type and pricingModelId
  type AvailabilityItem = any & {
    __index?: number;
  };

  type AvailabilityGroup = {
    key: string;
    chaletType: "Big" | "Small";
    pricingModelId: number | null;
    pricingModelDescription?: string;
    maxPersons?: number;
    items: AvailabilityItem[];
  };

  const groupedAvailability: AvailabilityGroup[] = useMemo(() => {
    if (!Array.isArray(availability) || availability.length === 0) return [];
    const map = new Map<string, AvailabilityGroup>();
    availability.forEach((item: any, idx: number) => {
      const type = normalizeChaletType(item.chaletType);
      const pmId = item.pricingModelId ?? null;
      const key = `${type}|${pmId}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          chaletType: type,
          pricingModelId: pmId,
          pricingModelDescription: item.pricingModelDescription,
          maxPersons: item.maxPersons,
          items: [],
        });
      }
      map.get(key)!.items.push({ ...item, __index: idx });
    });
    // Sort groups: Big first, then Small; stable by description then pmId
    return Array.from(map.values()).sort((a, b) => {
      if (a.chaletType !== b.chaletType) return a.chaletType === "Big" ? -1 : 1;
      const ad = a.pricingModelDescription || "";
      const bd = b.pricingModelDescription || "";
      if (ad.toLowerCase() < bd.toLowerCase()) return -1;
      if (ad.toLowerCase() > bd.toLowerCase()) return 1;
      const ap = a.pricingModelId ?? -1;
      const bp = b.pricingModelId ?? -1;
      return ap - bp;
    });
  }, [availability]);

  // Each chalet type (Big/Small) can have several pricing-model cards below (e.g. a 4-person
  // and a 3-person tier for Big), and every one of those cards shares the SAME physical pool
  // of chalets for that type - it is NOT a separate pool. Seeing "(5/5)" repeated on several
  // cards therefore does not mean there are only 5 chalets in total; it means 5 Big chalets and
  // 5 Small chalets (shown per-tier) are being reused across the tiers of their own type. Surface
  // the real total once, up front, so it can't be misread as "5 chalets total".
  const chaletTypeTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    groupedAvailability.forEach((g) => {
      const t = g.items[0]?.totalChalets ?? g.items.length;
      totals[g.chaletType] = Math.max(totals[g.chaletType] ?? 0, t);
    });
    return totals;
  }, [groupedAvailability]);

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDateChange = (update: [Date | null, Date | null]) => {
    const [start, end] = update;
    if (!bookOutsidePackage && start && !end) {
      // Package mode: default to 7 nights when only start is picked
      const defaultEnd = new Date(start);
      defaultEnd.setDate(defaultEnd.getDate() + 7);
      setDateRange({ startDate: start, endDate: defaultEnd });
      setNumberOfNights(7);
    } else {
      setDateRange({ startDate: start, endDate: end });
      if (start && end) {
        const diff = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        if (diff > 0) setNumberOfNights(diff);
      }
    }
  };

  const handleNightsChange = (nights: number) => {
    setNumberOfNights(nights);
    if (dateRange.startDate) {
      const newEnd = new Date(dateRange.startDate);
      newEnd.setDate(newEnd.getDate() + nights);
      setDateRange({ startDate: dateRange.startDate, endDate: newEnd });
    }
  };

  const handleUserSearch = async () => {
    const trimmedSearchTerm = localEmail.trim();
    if (!trimmedSearchTerm) {
      setEmailError("Please enter a valid search term.");
      return;
    }

    try {
      const response = await axios.get(
        `${apiUrl}/Booking/searchUser/${encodeURIComponent(trimmedSearchTerm)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      const responseData = response.data as { message: string; data?: any };
      if (
        responseData.message ===
        "Private user not found. You can add a new private user."
      ) {
        setFormVisible(true);
        setUserType("private");
        setPrivateUser({ email: trimmedSearchTerm } as PrivateUser);
        setResponseEmail(trimmedSearchTerm); // Store the email from the response
        setUserFound(false);
        setEmailError("");
      } else if (responseData.data) {
        const user = responseData.data;
        setFormVisible(false); // Hide the form when showing search results
        setUserType(user.isBusinessUser ? "business" : "private");
        if (user.isBusinessUser) {
          setBusinessUser(user as BusinessUser);
        } else {
          setPrivateUser(user as PrivateUser);
        }
        setResponseEmail(user.email); // Store the email from the response
        setUserFound(true);
        setEmailError("");
      } else {
        setEmailError("User not found.");
        setUserFound(false);
      }
    } catch (error) {
      console.error("Error searching user", error);
      setEmailError("Error searching user.");
      setUserFound(false);
    }
  };

  const validatePhoneNumber = (phoneNumber: string) => {
    if (!phoneNumber || !isValidPhoneNumber(phoneNumber)) {
      setPhoneNumberError("Please enter a valid phone number.");
    } else {
      setPhoneNumberError("");
      if (userType === "business") {
        setBusinessUser((prevBusinessUser: BusinessUser) => ({
          ...prevBusinessUser,
          phoneNumber,
        }));
      } else {
        setPrivateUser((prevPrivateUser: PrivateUser) => ({
          ...prevPrivateUser,
          phoneNumber,
        }));
      }
    }
  };

  const handlePhoneNumberChange = (phoneNumber: string) => {
    validatePhoneNumber(phoneNumber);
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    if (userType === "business") {
      setBusinessUser((prevBusinessUser: BusinessUser) => ({
        ...prevBusinessUser,
        [name]: value,
      }));
    } else {
      setPrivateUser((prevPrivateUser: PrivateUser) => ({
        ...prevPrivateUser,
        [name]: value,
      }));
    }
  };

  const addToCart = () => {
    if (!responseEmail || !userFound) {
      setEmailError(
        "Please search and select a customer before adding to cart."
      );
      return;
    }

    // CRITICAL: Require chalet type when booking outside of a package
    if (bookOutsidePackage && !chaletType) {
      setAvailabilityError('Please select a chalet type (Big or Small) before adding to cart.');
      return;
    }

    const sessionId = localStorage.getItem("sessionId");
    if (!sessionId) {
      console.error("SessionId is not found in localStorage");
      return;
    }

    const newBookings: ManagerBookingRequest[] = bookOutsidePackage
      ? [
          {
            PackageId: selectedPackage ? selectedPackage.id : null,
            PricingModelId: null,
            PackageName: selectedPackage
              ? selectedPackage.name
              : "Outside of package",
            ArrivalDate: toDateOnlyString(dateRange.startDate),
            DepartureDate: toDateOnlyString(dateRange.endDate),
            NumberOfPersons: numberOfPersonsToAdd,
            IsBusinessUser: userType === "business",
            Email: responseEmail,
            CompanyName: userType === "business" ? businessUser.name : null,
            CustomerName:
              userType === "business" ? businessUser.name : privateUser.name,
             GuestNames: guestNames.length > 0 ? guestNames : ["No guests provided"],
            PhoneNumber:
              userType === "business"
                ? businessUser.phoneNumber
                : privateUser.phoneNumber,
            Address:
              userType === "business"
                ? businessUser.address
                : privateUser.address,
            City:
              userType === "business" ? businessUser.city : privateUser.city,
            State: userType === "business" ? "" : privateUser.state,
            ZipCode:
              userType === "business"
                ? businessUser.zipCode
                : privateUser.zipCode,
            Country:
              userType === "business"
                ? businessUser.country
                : privateUser.country,
            SessionId: sessionId,
            TotalPrice: totalPrice,
            PricingModelDescription: "",
            BookingDescription: description,
            MainImageIndex: 0, // Set default mainImageIndex
            ChaletType: chaletType ? Number(chaletType) : null, // REQUIRED: 0 = Big, 1 = Small
          },
        ]
      : selectedAvailabilities.map((index) => {
          const selectedAvailability = availability[index];
          return {
            PackageId: selectedAvailability.packageId,
            PricingModelId: selectedAvailability.pricingModelId,
            PackageName: selectedAvailability.packageName,
            ArrivalDate: toDateOnlyString(dateRange.startDate),
            DepartureDate: toDateOnlyString(dateRange.endDate),
            NumberOfPersons: selectedAvailability.maxPersons,
            IsBusinessUser: userType === "business",
            Email: responseEmail,
            CompanyName: userType === "business" ? businessUser.name : null,
            CustomerName:
              userType === "business" ? businessUser.name : privateUser.name,
            GuestNames: guestNames.length > 0 ? guestNames : ["No guests provided"],
            PhoneNumber:
              userType === "business"
                ? businessUser.phoneNumber
                : privateUser.phoneNumber,
            Address:
              userType === "business"
                ? businessUser.address
                : privateUser.address,
            City:
              userType === "business" ? businessUser.city : privateUser.city,
            State: userType === "business" ? "" : privateUser.state,
            ZipCode:
              userType === "business"
                ? businessUser.zipCode
                : privateUser.zipCode,
            Country:
              userType === "business"
                ? businessUser.country
                : privateUser.country,
            SessionId: sessionId,
            TotalPrice: getDisplayPrice(selectedAvailability.totalPrice),
            PricingModelDescription:
              selectedAvailability.pricingModelDescription,
            BookingDescription: description,
            MainImageIndex: selectedAvailability.mainImageIndex || 0, // Ensure mainImageIndex is set
            ChaletType: chaletType ? Number(chaletType) : null, // Include chalet type
          };
        });

    setShoppingCart([...shoppingCart, ...newBookings]);

    // Only update the total price if it hasn't been manually changed
    if (totalPrice === 0) {
      const total = newBookings.reduce(
        (sum, booking) => sum + booking.TotalPrice,
        0
      );
      setTotalPrice(total);
    }
  };

  const handleRemoveFromCart = (index: number) => {
    const updatedCart = shoppingCart.filter((_, i) => i !== index);
    setShoppingCart(updatedCart);
  };

  const handleCancel = () => {
    setFormVisible(false);
    setUserFound(false);
    setEmailError("");
    setLocalEmail("");
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting) {
      return; // Guard against double-submission (e.g. double-click or slow connection)
    }
    if (bookOutsidePackage && (!dateRange.startDate || !dateRange.endDate)) {
      setAvailabilityError(
        "Please select a date range for booking outside of a package."
      );
      return;
    }

    // CRITICAL VALIDATION: Ensure all cart items have a chalet type assigned
    const itemsWithoutChaletType = shoppingCart.filter(item => item.ChaletType === null || item.ChaletType === undefined);
    if (itemsWithoutChaletType.length > 0) {
      setSubmitError('ERROR: One or more bookings do not have a chalet type assigned. Please select Big or Small chalet type for each booking before submitting.');
      return;
    }

    const requests: ManagerBookingRequest[] = shoppingCart.map((item) => ({
      PackageId: item.PackageId,
      PricingModelId: item.PricingModelId,
      PackageName: item.PackageName,
      ArrivalDate: toDateOnlyString(item.ArrivalDate),
      DepartureDate: toDateOnlyString(item.DepartureDate),
      NumberOfPersons: item.NumberOfPersons,
      IsBusinessUser: item.IsBusinessUser,
      Email: item.Email,
      CustomerName: item.CustomerName,
      GuestNames: item.GuestNames, // Include guest names
      PhoneNumber: item.PhoneNumber,
      Address: item.Address,
      City: item.City,
      State: item.State,
      ZipCode: item.ZipCode,
      Country: item.Country,
      SessionId: item.SessionId,
      TotalPrice: item.TotalPrice,
      PricingModelDescription: item.PricingModelDescription,
      BookingDescription: item.BookingDescription,
      ChaletType: Number(chaletType), // Ensure chalet type is sent as a number
    }));

    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${apiUrl}/PackageBooking/manager-booking`,
        requests,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      if (response.status === 200) {
        const responseData = response.data;
        console.log("Booking response:", responseData);
        navigate("/booking-confirmation", {
          state: { orderDetails: responseData },
        }); // Navigate to confirmation page
        setFormVisible(false); // Hide the form on successful submission
      } else {
        setSubmitError(`Booking failed: ${response.statusText}`);
      }
    } catch (error: any) {
      console.error("Error booking package", error);
      console.error('Response status:', error?.response?.status);
      console.error('Response data:', JSON.stringify(error?.response?.data, null, 2));
      // Extract server-provided message with enhanced detail extraction
      const status = error?.response?.status;
      const data = error?.response?.data;
      
      // Build a detailed error message from various possible formats
      const extractErrorDetails = (): string => {
        if (!data) {
          return error?.message || 'Unknown error occurred';
        }
        
        // Handle plain string response
        if (typeof data === 'string') {
          return data;
        }
        
        const parts: string[] = [];
        
        // ASP.NET Core Problem Details format
        if (data.title) {
          parts.push(data.title);
        }
        if (data.detail) {
          parts.push(data.detail);
        }
        
        // Standard message field
        if (data.message && !parts.includes(data.message)) {
          parts.push(data.message);
        }
        
        // Validation errors object
        if (data.errors && typeof data.errors === 'object') {
          const errorMessages = Object.entries(data.errors)
            .map(([field, messages]) => {
              const msgs = Array.isArray(messages) ? messages.join(', ') : String(messages);
              return `${field}: ${msgs}`;
            })
            .join('; ');
          if (errorMessages) {
            parts.push(`Validation: ${errorMessages}`);
          }
        }
        
        // Inner exception details
        if (data.innerException) {
          parts.push(`Details: ${data.innerException}`);
        }
        
        if (parts.length === 0) {
          return error?.message || 'Booking failed - server returned an error';
        }
        
        return parts.join(' | ');
      };

      // Map backend messages to friendlier text
      // Only map "session expired" for actual 401 responses, not 500s that mention "unauthorized"
      const mapServerMessage = (msg: string, httpStatus?: number): string => {
        if (!msg) return '';
        const lower = msg.toLowerCase();
        if (lower.includes('no available chalets')) {
          return 'Those dates are fully booked for the selected option. Try different dates or choose another chalet type.';
        }
        if (lower.includes('not enough availability')) {
          return 'The group size exceeds the remaining availability for those dates.';
        }
        if (lower.includes('date range not found')) {
          return 'The selected package does not cover these dates. Pick dates within the package window.';
        }
        if (httpStatus === 401 || (httpStatus !== 500 && (lower.includes('unauthorized') || lower.includes('401')))) {
          return 'Your session has expired. Please log in again.';
        }
        return '';
      };

      const serverMessage = extractErrorDetails();
      const friendly = mapServerMessage(serverMessage, status);
      const dateHint = dateRange.startDate && dateRange.endDate
        ? ` (Selected dates: ${dateRange.startDate.toLocaleDateString()} – ${dateRange.endDate.toLocaleDateString()})`
        : '';
      
      const statusHint = status === 500
        ? ' This may be a temporary server issue. Try refreshing and submitting again.'
        : '';

      const composed = friendly
        ? `${friendly}${dateHint}${statusHint}`
        : serverMessage
          ? `${serverMessage}${dateHint}${statusHint}`
          : `Booking failed. Please review the dates and options.${statusHint}`;

      const withStatus = status ? `[Error ${status}] ${composed}` : composed;
      setSubmitError(withStatus);
    } finally {
      setIsSubmitting(false);
    }
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleAvailabilitySelect = (index: number) => {
    setSelectedAvailability(index);
  };

  const handleCheckboxChange = (index: number) => {
    setSelectedAvailabilities((prevSelected) => {
      if (prevSelected.includes(index)) {
        return prevSelected.filter((i) => i !== index); // Deselect if already selected
      } else {
        return [...prevSelected, index]; // Select the new index
      }
    });
  };

  useEffect(() => {
    console.log("form visible:", formVisible);
  }, [formVisible]);

  const handleAddPrivateUser = () => {
    console.log("Adding private user");
    setFormVisible(true);
    setUserType("private");
    setPrivateUser({ email: localEmail } as PrivateUser);
    setResponseEmail(localEmail);
    setUserFound(false);
    setEmailError("");
  };

  const handleAddBusinessUser = () => {
    setFormVisible(true);
    setUserType("business");
    setBusinessUser({ email: localEmail } as BusinessUser);
    setResponseEmail(localEmail);
    setUserFound(false);
    setEmailError("");
  };

  const handleAddUser = async () => {
    try {
      const user = userType === "business" ? businessUser : privateUser;
      const endpoint =
        userType === "business"
          ? "BusinessUser/addBusinessUser"
          : "PrivateUser/userInfo";
      const response = await axios.post(`${apiUrl}/${endpoint}`, user, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });

      if (response.status === 200) {
        alert("User added successfully");
        setFormVisible(false);
      } else {
        alert(`Failed to add user: ${response.statusText}`);
      }
    } catch (error: any) {
      console.error("Error adding user", error);
      alert(`Error adding user: ${error.message}`);
    }
  };

  useEffect(() => {
    console.log("form visible:", formVisible);
  }, [formVisible]);

  const handleTotalPriceChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = Number(event.target.value);
    if (!isNaN(value) && value >= 0) {
      setTotalPrice(value);
    }
  };

  const handleChaletTypeChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setChaletType(event.target.value);
  };

  return (
    <div className="container simple-booking-form mb-5 pb-5">
      <div className="form-group">
        <label>Book outside of a package?</label>
        <div className="radio-group">
          <input
            type="radio"
            id="outsidePackageYes"
            name="outsideOfPackage"
            value="yes"
            checked={bookOutsidePackage === true}
            onChange={() => setBookOutsidePackage(true)}
            className="radio-input"
          />
          <label htmlFor="outsidePackageYes" className="radio-label">
            Yes
          </label>
        </div>
        <div className="radio-group">
          <input
            type="radio"
            id="outsidePackageNo"
            name="outsideOfPackage"
            value="no"
            checked={bookOutsidePackage === false}
            onChange={() => setBookOutsidePackage(false)}
            className="radio-input"
          />
          <label htmlFor="outsidePackageNo" className="radio-label">
            No
          </label>
        </div>
      </div>
      {!bookOutsidePackage && (
        <>
          <div className="form-group">
            <label htmlFor="package">
              Search a package and pricing options for availability
            </label>
            <select
              id="package"
              name="package"
              value={packageId ?? ""}
              onChange={(e) => setPackageId(Number(e.target.value))}
              className="form-control"
            >
          <option value="" disabled>
            Select a package
          </option>
          {packages.map((pkg) => (
            <option key={pkg.id} value={pkg.id}>
              {pkg.name}
            </option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label htmlFor="dateRange">Arrival & Departure Dates</label>
        <div className="date-picker-wrapper">
          <DatePicker
            selected={dateRange.startDate}
            onChange={handleDateChange}
            startDate={dateRange.startDate ?? undefined}
            endDate={dateRange.endDate ?? undefined}
            selectsRange
            className="form-control"
            placeholderText="Select dates"
            dateFormat="EEE MMM d"
            locale={locale}
            calendarStartDay={1} // Start the calendar on Monday
          />
        </div>
      </div>
      <button
        type="button"
        className="btn btn-primary mb-3"
        onClick={handleCheckAvailability}
        disabled={packageId === null}
        title={packageId === null ? "Select a package first" : undefined}
      >
        Check Availability
      </button>
      {availabilityError && (
        <div className="text-danger">{availabilityError}</div>
      )}
      {hasCheckedAvailability && groupedAvailability.length === 0 && !availabilityError && (
        <div className="alert alert-warning mt-3" role="alert">
          <strong>No availability found.</strong> There are no available chalets or pricing models for the selected package and dates. Try selecting different dates or another package.
        </div>
      )}
      {groupedAvailability.length > 0 && (
        <div className="availability-results">
          <h3>Availability Results:</h3>
          <p className="text-muted small mb-3">
            Property inventory: {chaletTypeTotals['Big'] ?? 0} Big chalet{(chaletTypeTotals['Big'] ?? 0) === 1 ? '' : 's'} + {chaletTypeTotals['Small'] ?? 0} Small chalet{(chaletTypeTotals['Small'] ?? 0) === 1 ? '' : 's'} = {(chaletTypeTotals['Big'] ?? 0) + (chaletTypeTotals['Small'] ?? 0)} chalets total.
            Each card below is a different <em>pricing tier</em> (by number of persons), not a separate chalet pool - tiers of the same type share the same physical chalets.
          </p>
          <div className="availability-groups">
            {groupedAvailability.map((group) => {
              const firstItem = group.items[0];
              const availableCount = firstItem?.availableCount ?? group.items.filter(
                (i) => i.availabilityStatus === "Available"
              ).length;
              const totalChalets = firstItem?.totalChalets ?? group.items.length;
              const bookedCount = totalChalets - availableCount;
              const allFullyBooked = availableCount === 0;
              const isExpanded = !!expandedGroups[group.key];
              return (
                <div key={group.key} className={`availability-group border rounded p-3 mb-3 ${allFullyBooked ? 'availability-group--booked' : 'availability-group--available'}`}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <div className="availability-group__title">
                        <span className={`availability-badge ${allFullyBooked ? 'availability-badge--booked' : 'availability-badge--available'}`}>
                          {allFullyBooked
                            ? `Fully Booked (${totalChalets}/${totalChalets})`
                            : `${bookedCount} booked · ${availableCount} available`}
                        </span>
                        <strong className="ms-2">{group.chaletType === 'Big' ? 'Large Chalet' : 'Small Chalet'}</strong>
                      </div>
                      <div className="availability-group__description mt-1">
                        {group.pricingModelDescription ?? 'Pricing model'}
                        {group.maxPersons ? (
                          <span className="text-muted ms-1">· up to {group.maxPersons} {group.maxPersons === 1 ? 'person' : 'persons'}</span>
                        ) : null}
                      </div>
                      {firstItem?.summary && (
                        <div className="availability-group__summary mt-1 text-muted small">
                          {firstItem.summary}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => toggleGroup(group.key)}
                    >
                      {isExpanded ? "Hide" : "Show"}
                    </button>
                  </div>
                  {isExpanded && (
                    <div className="availability-items mt-3">
                      {group.items.map((item) => {
                        const isAvailable = item.availabilityStatus === "Available";
                        const slotLabel = item.__slotNumber > 0 ? `Chalet ${item.__slotNumber}` : null;
                        return (
                          <div key={item.__index} className={`availability-item d-flex align-items-center gap-3 p-2 rounded mb-2 ${isAvailable ? 'availability-item--available' : 'availability-item--booked'}`}>
                            <input
                              type="checkbox"
                              name="availability"
                              className="availability-checkbox"
                              value={item.__index}
                              checked={selectedAvailabilities.includes(item.__index)}
                              onChange={() => handleCheckboxChange(item.__index)}
                              disabled={!isAvailable}
                            />
                            <div className="availability-item__details">
                              {slotLabel && (
                                <span className="availability-item__slot fw-semibold me-2">{slotLabel}</span>
                              )}
                              <span className="availability-item__price">
                                €{Number(getDisplayPrice(item.totalPrice)).toLocaleString()}
                              </span>
                              <span className="availability-item__per-night text-muted ms-1">
                                (€{Number(getDisplayPrice(item.pricePerNight)).toLocaleString()}/night)
                              </span>
                            </div>
                            <span className={`availability-status-dot ${isAvailable ? 'availability-status-dot--available' : 'availability-status-dot--booked'}`}>
                              {isAvailable ? 'Available' : 'Fully Booked'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
        </>
      )}
      {bookOutsidePackage && (
        <>
          <div className="form-group">
            <label htmlFor="packageSelect">Select a Package ? (optional)</label>
            <select
              id="packageSelect"
              name="packageSelect"
              value={selectedPackage ? selectedPackage.id : ""}
              onChange={(e) => {
                const selectedPkg = availablePackages.find(
                  (pkg) => pkg.id === Number(e.target.value)
                );
                setSelectedPackage(selectedPkg || null);
              }}
              className="form-control"
            >
              <option value="" disabled>
                Select a package
              </option>
              {availablePackages.map((pkg) => (
                <option key={pkg.id} value={pkg.id}>
                  {pkg.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="dateRange">Arrival & Departure Dates</label>
            <div className="date-picker-wrapper">
              <DatePicker
                selected={dateRange.startDate}
                onChange={handleDateChange}
                startDate={dateRange.startDate ?? undefined}
                endDate={dateRange.endDate ?? undefined}
                selectsRange
                className="form-control"
                placeholderText="Select dates"
                dateFormat="EEE MMM d"
                locale="en"
                calendarStartDay={1} // Start the calendar on Monday
              />
            </div>
          </div>
          <div className="form-group mt-2">
            <label htmlFor="numberOfNightsOutside">Number of nights</label>
            <select
              id="numberOfNightsOutside"
              className="form-control"
              value={numberOfNights}
              onChange={(e) => handleNightsChange(Number(e.target.value))}
            >
              {Array.from({ length: 14 }, (_, i) => i + 1).map(n => (
                <option key={n} value={n}>{n} {n === 1 ? 'night' : 'nights'}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="numberOfPersonsToAdd">Number of Persons</label>
            <input
              type="number"
              id="numberOfPersonsToAdd"
              name="numberOfPersonsToAdd"
              value={numberOfPersonsToAdd}
              onChange={(e) =>
                setNumberOfPersonsToAdd(Math.max(1, Number(e.target.value)))
              } // Disable negative numbers
              className="form-control"
              min="1"
            />
          </div>
          <div className="form-group">
            <label htmlFor="totalPrice">Total Price</label>
            <input
              type="number"
              id="totalPrice"
              name="totalPrice"
              value={totalPrice}
              onChange={handleTotalPriceChange} // Use the new handler
              className="form-control"
              min="0"
            />
          </div>
        </>
      )}
      <div className="form-group">
        <label htmlFor="description">Booking Description</label>
        <textarea
          id="description"
          name="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="form-control"
        />
      </div>
      <div className="form-group mb-1">
        <label htmlFor="email">Customer Email, Name, or Business Code</label>
        <input
          type="text"
          id="email"
          name="email"
          value={localEmail}
          onChange={(e) => setLocalEmail(e.target.value)}
          className="form-control"
        />
        <button
          type="button"
          className="btn btn-primary mt-3 mb-2"
          onClick={handleUserSearch}
        >
          Search User
        </button>
        {emailError && <div className="text-danger">{emailError}</div>}
        {!userFound && (
          <div className="mt-3">
            <button
              type="button"
              className="btn btn-secondary mr-2"
              onClick={handleAddPrivateUser}
            >
              Add Private User
            </button>
            <button
              type="button"
              className="btn btn-secondary ms-2"
              onClick={handleAddBusinessUser}
            >
              Add Business User
            </button>
          </div>
        )}
      </div>
      {userFound && (
        <div className="user-details">
          <h3>User Details</h3>
          {userType === "business" && businessUser.companyName && (
            <p>Company Name: {businessUser.companyName}</p>
          )}
          <p>
            Name:{" "}
            {userType === "business" ? businessUser.name : privateUser.name}
          </p>
          <p>
            Email:{" "}
            {userType === "business" ? businessUser.email : privateUser.email}
          </p>
          <p>
            Phone:{" "}
            {userType === "business"
              ? businessUser.phoneNumber
              : privateUser.phoneNumber}
          </p>
          <p>
            Address:{" "}
            {userType === "business"
              ? businessUser.address
              : privateUser.address}
          </p>
          <p>
            City:{" "}
            {userType === "business" ? businessUser.city : privateUser.city}
          </p>
          <p>
            Country:{" "}
            {userType === "business"
              ? businessUser.country
              : privateUser.country}
          </p>
        </div>
      )}
      {formVisible && (
        <>
          <div className="form-group">
            <label htmlFor="userType">User Type</label>
            <select
              id="userType"
              name="userType"
              value={userType}
              onChange={(e) =>
                setUserType(e.target.value as "private" | "business")
              }
              className="form-control"
            >
              <option value="private">Private</option>
              <option value="business">Business</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="phoneNumber">Phone Number</label>
            <PhoneInput
              id="phoneNumber"
              name="phoneNumber"
              value={
                userType === "business"
                  ? businessUser.phoneNumber
                  : privateUser.phoneNumber
              }
              onChange={handlePhoneNumberChange as any}
              defaultCountry={defaultCountry as any}
            />
            {phoneNumberError && (
              <div className="text-danger">{phoneNumberError}</div>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="customerName">
              {userType === "business" ? "Business Contact Name (from account) — add real Guest Names below" : "Customer Name"}
            </label>
            <input
              type="text"
              id="customerName"
              name="name"
              value={
                userType === "business" ? businessUser.name : privateUser.name
              }
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label htmlFor="customerEmail">Customer Email</label>
            <input
              type="email"
              id="customerEmail"
              name="email"
              value={
                userType === "business" ? businessUser.email : privateUser.email
              }
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label htmlFor="address">Street Address</label>
            <input
              type="text"
              id="address"
              name="address"
              value={
                userType === "business"
                  ? businessUser.address
                  : privateUser.address
              }
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label htmlFor="address2">Address 2 (optional)</label>
            <input
              type="text"
              id="address2"
              name="address2"
              value={
                userType === "business"
                  ? businessUser.address2
                  : privateUser.address2
              }
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label htmlFor="zipcode">Zip Code</label>
            <input
              type="text"
              id="zipCode"
              name="zipCode"
              value={
                userType === "business"
                  ? businessUser.zipCode
                  : privateUser.zipCode
              }
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label htmlFor="city">City</label>
            <input
              type="text"
              id="city"
              name="city"
              value={
                userType === "business" ? businessUser.city : privateUser.city
              }
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label htmlFor="country">Country</label>
            <input
              type="text"
              id="country"
              name="country"
              value={
                userType === "business"
                  ? businessUser.country
                  : privateUser.country
              }
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
          <button
            type="button"
            className="btn btn-primary mb-3"
            onClick={handleAddUser}
          >
            Add {userType === "business" ? "Business" : "Private"} User
          </button>
          <button
            type="button"
            className="btn btn-secondary mb-3"
            onClick={handleCancel}
          >
            Cancel
          </button>
        </>
      )}
      <div className="form-group">
        <label htmlFor="totalPrice">Total Price</label>
        <input
          type="number"
          id="totalPrice"
          name="totalPrice"
          value={totalPrice}
          onChange={handleTotalPriceChange}
          className="form-control"
        />
        <small className="form-text text-muted">
          Note: The total price does not include bookings outside of packages.
        </small>
      </div>
      <div className="form-group">
        <label htmlFor="chaletType">Chalet Type</label>
        <select
          id="chaletType"
          name="chaletType"
          value={chaletType}
          onChange={handleChaletTypeChange}
          className="form-control"
        >
          <option value="" disabled>
            Select a chalet type
          </option>
          {chaletTypes.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label htmlFor="guestNames">Guest Names (comma separated)</label>
        <input
          type="text"
          id="guestNames"
          name="guestNames"
          value={guestNamesInput}
          onChange={(e) => setGuestNamesInput(e.target.value)}
          className="form-control"
          placeholder="e.g. John Doe, Jane Doe"
        />
        <button
          type="button"
          className="btn btn-secondary mt-2"
          onClick={() =>
            setGuestNames(
              guestNamesInput
                .split(",")
                .map((n) => n.trim())
                .filter((n) => n)
            )
          }
        >
          Add Guest Names
        </button>
        {guestNames.length > 0 && (
          <div className="mt-2">
            <strong>Guests:</strong> {guestNames.join(", ")}
          </div>
        )}
      </div>
      <button
        type="button"
        className="btn btn-secondary mb-3"
        onClick={addToCart}
      >
        Add to Cart
      </button>
      {shoppingCart.length > 0 && (
        <>
          <div className="shopping-cart">
            <h3>Shopping Cart</h3>
            <ul>
              {shoppingCart.map((item, index) => (
                <li key={index}>
                  {item.CustomerName} - {toDisplayDate(item.ArrivalDate)} to {toDisplayDate(item.DepartureDate)},
                  {item.PackageName} {item.PricingModelDescription} Total Price:
                  €{Number(item.TotalPrice).toLocaleString()}
                  <button
                    type="button"
                    className="btn btn-danger ml-2"
                    onClick={() => handleRemoveFromCart(index)}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          </div>
          {submitError && (
            <div className="alert alert-danger" role="alert">
              {submitError}
            </div>
          )}
          <button
            type="submit"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting…' : 'Confirm Bookings'}
          </button>
        </>
      )}
    </div>
  );
};

export default SimpleBookingForm;
